import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Avatar,
  Box,
  Card,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  TableSortLabel,
  Chip,
} from "@mui/material";
import apiService from "../../../../../service/Api";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LeaderboardEntry {
  position: number;
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  all_time_total: number;
  ytd_rank: number;
  mtd_rank: number | null;
  venues_ytd: number;
  this_month: number;
  last_month: number;
  mom_change: number | null;
}

interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  total_participants: number;
  as_of: string;
  periods: {
    ytd_start: string;
    mtd_start: string;
    last_month_start: string;
    last_month_end: string;
  };
}

interface UserVenue {
  id: number;
  name: string;
  venue_type?: string;
  files?: { file: string; is_cover?: boolean }[];
  suburb?: string;
  address_one?: string;
  city?: string;
  created_at: string;
}

interface VenuesApiResponse {
  results: UserVenue[];
}

type SortField =
  | "name"
  | "allTimeTotal"
  | "ytdRank"
  | "mtdRank"
  | "venuesThisMonth"
  | "venuesLastMonth"
  | "venuesYTD"
  | "monthChange";

type SortDirection = "asc" | "desc";

// ─── Component ────────────────────────────────────────────────────────────────

const VenueLeaderboard2: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState<SortField>("allTimeTotal");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const [selectedEntry, setSelectedEntry] = useState<LeaderboardEntry | null>(
    null,
  );
  const [userVenues, setUserVenues] = useState<UserVenue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [modalPage, setModalPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const navigate = useNavigate();

  // ── Fetch ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const res = await apiService.get<LeaderboardResponse>(
          `/api/management/dashboard/venues-leaderboard/`,
        );
        setLeaderboard(res.data.leaderboard ?? []);
        setAsOf(res.data.as_of ?? null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  // ── Sort ────────────────────────────────────────────────────────────────────

  const sortedRows = useMemo(() => {
    return [...leaderboard].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "name":
          cmp = a.full_name.localeCompare(b.full_name);
          break;
        case "allTimeTotal":
          cmp = a.all_time_total - b.all_time_total;
          break;
        case "ytdRank":
          cmp = a.ytd_rank - b.ytd_rank;
          break;
        // nulls (no MTD activity) always go to the bottom regardless of direction
        case "mtdRank": {
          if (a.mtd_rank === null && b.mtd_rank === null) cmp = 0;
          else if (a.mtd_rank === null) cmp = 1;
          else if (b.mtd_rank === null) cmp = -1;
          else cmp = a.mtd_rank - b.mtd_rank;
          return sortDir === "asc" ? cmp : -cmp;
        }
        case "venuesYTD":
          cmp = a.venues_ytd - b.venues_ytd;
          break;
        case "venuesThisMonth":
          cmp = a.this_month - b.this_month;
          break;
        case "venuesLastMonth":
          cmp = a.last_month - b.last_month;
          break;
        case "monthChange":
          cmp = (a.mom_change ?? 0) - (b.mom_change ?? 0);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [leaderboard, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(
        field === "ytdRank" || field === "mtdRank" || field === "name"
          ? "asc"
          : "desc",
      );
    }
  };

  // ── Modal ───────────────────────────────────────────────────────────────────

  const handleUserClick = useCallback(async (entry: LeaderboardEntry) => {
    setSelectedEntry(entry);
    setModalPage(0);
    setVenuesLoading(true);

    try {
      const res = await apiService.get<VenuesApiResponse>(
        `/api/venues/venue/?created_by=${entry.id}&page_size=1000`,
      );
      setUserVenues(res.data.results ?? []);
    } catch {
      setUserVenues([]);
    } finally {
      setVenuesLoading(false);
    }
  }, []);

  const paginatedVenues = userVenues.slice(
    modalPage * rowsPerPage,
    modalPage * rowsPerPage + rowsPerPage,
  );

  // ── UI helpers ──────────────────────────────────────────────────────────────

  const rankBadge = (rank: number | null) => {
    if (rank === null)
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );

    const medals: Record<number, { bg: string; color: string }> = {
      1: { bg: "#FFD700", color: "#7a5a00" },
      2: { bg: "#C0C0C0", color: "#444" },
      3: { bg: "#CD7F32", color: "#fff" },
    };
    const style = medals[rank] ?? { bg: "#f0f0f0", color: "#555" };
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          bgcolor: style.bg,
          color: style.color,
          fontWeight: 700,
          fontSize: "0.75rem",
        }}
      >
        {rank}
      </Box>
    );
  };

  const changeChip = (val: number | null) => {
    if (val === null || val === 0)
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    return (
      <Chip
        label={val > 0 ? `+${val}` : val}
        size="small"
        sx={{
          fontWeight: 700,
          fontSize: "0.75rem",
          bgcolor: val > 0 ? "#e6f9f0" : "#fff0f0",
          color: val > 0 ? "#1a7a4a" : "#c0392b",
          border: `1px solid ${val > 0 ? "#a8e6c8" : "#f5b8b0"}`,
        }}
      />
    );
  };

  const SortCell = ({
    field,
    label,
    align = "left",
  }: {
    field: SortField;
    label: string;
    align?: "left" | "center" | "right";
  }) => (
    <TableCell align={align} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            Venue Leaderboard
          </Typography>
          {!loading && (
            <Typography variant="body2" color="text.secondary">
              {sortedRows.length} creator{sortedRows.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>

        {asOf && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: "block" }}
          >
            As of {dayjs(asOf).format("D MMM YYYY, HH:mm")}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f7f7f7" }}>
                  <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                  <SortCell field="name" label="Name" />
                  <SortCell
                    field="allTimeTotal"
                    label="All-Time Total"
                    align="center"
                  />
                  <SortCell field="ytdRank" label="YTD Rank" align="center" />
                  <SortCell field="mtdRank" label="MTD Rank" align="center" />
                  <SortCell
                    field="venuesYTD"
                    label="Venues YTD"
                    align="center"
                  />
                  <SortCell
                    field="venuesThisMonth"
                    label="This Month"
                    align="center"
                  />
                  <SortCell
                    field="venuesLastMonth"
                    label="Last Month"
                    align="center"
                  />
                  <SortCell
                    field="monthChange"
                    label="MoM Change"
                    align="center"
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ py: 4, color: "text.secondary" }}
                    >
                      No leaderboard data available.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((entry, index) => (
                    <TableRow
                      key={entry.id}
                      hover
                      sx={{ cursor: "pointer", height: 56 }}
                      onClick={() => handleUserClick(entry)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                        >
                          {index + 1}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {entry.full_name || entry.email}
                        </Typography>
                        {entry.full_name && (
                          <Typography variant="caption" color="text.secondary">
                            {entry.email}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Typography fontWeight={700}>
                          {entry.all_time_total}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {rankBadge(entry.ytd_rank)}
                      </TableCell>
                      <TableCell align="center">
                        {rankBadge(entry.mtd_rank)}
                      </TableCell>

                      <TableCell align="center">
                        <Typography fontWeight={600}>
                          {entry.venues_ytd}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography>{entry.this_month}</Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography color="text.secondary">
                          {entry.last_month}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        {changeChip(entry.mom_change)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* ── Venue Detail Modal ── */}
      <Dialog
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedEntry?.full_name || selectedEntry?.email}&apos;s All Venues
        </DialogTitle>
        <DialogContent>
          {venuesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : userVenues.length === 0 ? (
            <Typography
              sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
            >
              No venues found.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f7f7f7" }}>
                    <TableCell>Image</TableCell>
                    <TableCell>Venue</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedVenues.map((venue) => (
                    <TableRow
                      key={venue.id}
                      hover
                      sx={{ cursor: "pointer", height: 56 }}
                      onClick={() => navigate(`/venues/view/${venue.id}`)}
                    >
                      <TableCell>
                        {venue.files?.[0]?.file ? (
                          <Avatar
                            variant="rounded"
                            src={venue.files[0].file}
                            sx={{ width: 36, height: 36 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: "grey.200",
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {venue.name || "Unnamed Venue"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {venue.address_one ??
                            venue.suburb ??
                            venue.city ??
                            "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {venue.venue_type || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(venue.created_at).format("D MMM YYYY")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={userVenues.length}
            page={modalPage}
            onPageChange={(_, p) => setModalPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setModalPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VenueLeaderboard2;
